import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { PackageCallout } from "@/components/PackageCallout";
import { Footer } from "@/components/Footer";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";

// Hero Image
import heroImage from "@/assets/stone-masonry-steps-verona-essex-county-nj-3.webp";
import stoneStepsFranklinLakesImage from "@/assets/stone-steps-masonry-franklin-lakes-nj.webp";
import stoneMontclairImage from "@/assets/stone-masonry-steps-installation-montclair-nj.webp";

// Citation link component - wraps text with citation number
const CitationLink = ({ num, url, children }: { num: number; url: string; children?: React.ReactNode }) => (
  <a 
    href={url} 
    target="_blank" 
    rel="noopener noreferrer" 
    className="text-secondary hover:underline"
  >
    {children}<sup>{num}</sup>
  </a>
);

const faqs = [
  {
    question: "Do I need a permit to install masonry steps in North Jersey?",
    answer: "Yes, most municipalities in Bergen, Passaic, Morris, and Essex counties require a construction permit under NJ's Uniform Construction Code (N.J.A.C. 5:23). Your contractor should pull the permit before work begins.",
  },
  {
    question: "How deep does the footing need to be for masonry steps in North Jersey?",
    answer: "The frost line in North Jersey is 36 inches, and footings must reach below that depth to prevent heaving. IRC R403 sets the minimum thickness at 8 inches on undisturbed soil.",
  },
  {
    question: "What type of mortar is best for exterior masonry steps?",
    answer: "Type S mortar is the standard for exterior step construction. It provides at least 1,800 PSI compressive strength per ASTM C270 and holds up well in wet and cold conditions.",
  },
  {
    question: "Can masonry steps be built in winter in North Jersey?",
    answer: "Yes, with precautions. Mortar should not be mixed or applied when temperatures are below 40°F and falling. The BIA recommends heated materials and wind barriers during cold weather masonry construction.",
  },
  {
    question: "How long do masonry steps last in North Jersey?",
    answer: "Properly built masonry steps with a footing below frost line, rebar reinforcement, and Type S mortar typically last 30 to 50 years in North Jersey's climate. Ongoing tuckpointing and sealing extend the lifespan further.",
  },
  {
    question: "Should I choose brick, stone, or block for my new front steps?",
    answer: "All three work well. Brick is the most common facing material in North Jersey neighborhoods. Natural stone offers a higher-end look. Block is durable and cost-effective. A masonry contractor can match the material to your home's style.",
  },
];

const citations = [
  {
    num: 1,
    text: "Mason Contractors Association of America (MCAA) — Technical Resources. Continuing education, code advocacy, safety standards, masonry certification, and construction best practices.",
    url: "https://masoncontractors.org/technical",
  },
  {
    num: 2,
    text: "Brick Industry Association (BIA) — Technical Notes on Brick Construction. Free bulletins on brick design, detailing, construction, mortar selection, and cold/hot weather masonry.",
    url: "https://gobrick.com/resources/technical-notes",
  },
  {
    num: 3,
    text: "ASTM International — C270: Standard Specification for Mortar for Unit Masonry. Five mortar types (M, S, N, O, K), proportion and property specifications, compressive strength requirements.",
    url: "https://store.astm.org/standards/c270",
  },
  {
    num: 4,
    text: "OSHA — 1926.1052: Stairways (Construction Standards). Riser/tread dimensions, handrail requirements, landing specifications, stairway safety regulations.",
    url: "https://osha.gov/laws-regs/regulations/standardnumber/1926/1926.1052",
  },
  {
    num: 5,
    text: "New Jersey DCA — Division of Codes and Standards. NJ Uniform Construction Code (N.J.A.C. 5:23), permit requirements, state amendments to IRC, local code enforcement.",
    url: "https://nj.gov/dca/codes",
  },
];

const youtubeShorts = [
  {
    url: "https://www.youtube.com/embed/c_50pnhg30w",
    title: "Masonry Steps Installation Video 1",
  },
  {
    url: "https://www.youtube.com/embed/g25VA4vng-Y",
    title: "Masonry Steps Installation Video 2",
  },
  {
    url: "https://www.youtube.com/embed/rqWp1V0r8sY",
    title: "Masonry Steps Installation Video 3",
  },
];

export default function StepsStoops() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Steps & Stoops Installation Totowa, NJ | Masonry Pro</title>
        <meta name="description" content="New masonry steps and stoops in brick, stone, and block, built on footings below the frost line. Serving Totowa and North Jersey. Free estimates given." />
        <meta property="og:description" content="New masonry steps and stoops in brick, stone, and block, built on footings below the frost line. Serving Totowa and North Jersey. Free estimates given." />
        <meta property="og:title" content="Steps & Stoops Installation Totowa, NJ | Masonry Pro" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Masonry Steps & Stoops Installation",
            description: "Expert masonry steps and stoops installation in North Jersey. Brick, stone, or block. Insured and permitted.",
            url: "/masonry-services/steps-stoops",
            image: "https://masonry-pro.com/og/steps-stoops.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/steps-stoops.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/steps-stoops.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section - Centered with lighter overlay */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Steps and stoops masonry construction in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(13,16,23,0.45), rgba(13,16,23,0.65))' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Steps & Stoops Installation in Totowa, NJ Serving <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>North Jersey</span>: Built on the Right Foundation
                </h1>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395">Schedule Your Free Assessment</a>
                </Button>
              </div>
            </div>
          </section>

          <PackageCallout packageId="curb-appeal" packageName="Curb Appeal Package" blurb={"Rebuild the front entry as one project: masonry steps and stoop tied into a paver walkway with stone veneer or brick details to match the house."} />

          {/* Intro Section with Citation */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto prose prose-lg">
                <p className="text-lg text-muted-foreground">
                  In North Jersey, front steps take a beating. The salt from the plows, water that freezes overnight, and people going in and out every single day. It all adds up. We install brick, stone, and block masonry steps for residential entries and walkways across Bergen, Passaic, Morris, and Essex counties. Most projects take 3 to 5 days once the footing is poured. And honestly, the footing is the part that matters most. Get that right, below the frost line with the right base, and your steps stay level for decades. That is what masonry steps installation in North Jersey comes down to. Steps and stoops are everyday work for a <Link to="/" className="text-secondary hover:underline">North Jersey masonry contractor</Link> like us.
                </p>
                <p className="text-lg text-muted-foreground mt-4">
                  A lot of the homes around Totowa, Clifton, and Paterson were built decades ago, and the front steps on those houses were often poured on footings that never went deep enough. Our frost line here runs about three feet down, so anything shallower will lift and settle every winter until the treads pull away from the house. When we build steps we dig below that depth first, because a stoop that moves is both a trip hazard and an expensive thing to fix twice. If you have ever wondered where the stoop came from in the first place, here is <Link to="/pro-tips/what-is-a-stoop" className="text-secondary hover:underline">the history behind the North Jersey stoop</Link>.
                </p>
              </div>
            </div>
          </section>

          {/* Footing Section - 2 Column */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12">
                {/* Every Masonry Step Needs a Footing */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Every Masonry Step Needs a Footing <span className="text-gradient-gold">Below Frost Line</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      If you are replacing crumbling front steps on a Colonial or Cape Cod in <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link> or <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link> County, it starts in the footing. A concrete footing anchors your steps to solid ground, preventing them from shifting or settling over time. It sits on undisturbed soil at least 8 inches thick per <CitationLink num={5} url="https://nj.gov/dca/codes">IRC R403</CitationLink> and must extend below 36 inches. That is the frost depth here in North Jersey.
                    </p>
                    <p>
                      If you were to pour steps on bare dirt, you would be asking for trouble. Soil moves and washes out over time. A compacted gravel base under the footing gives you drainage and a stable surface to build on. Clay soils in towns like Wayne and <Link to="/service-areas/passaic-county/clifton" className="text-secondary hover:underline">Clifton</Link> make this even more of a factor. Clay holds more water, and when that water freezes, the pressure pushes everything upward.
                    </p>
                  </div>
                </div>

                {/* Isolation Joint */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Proper Separation <span className="text-gradient-gold">Prevents Cracking</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      One question many homeowners ask is whether the steps should be attached directly to the house foundation. They shouldn't be. We use an isolation joint between the steps and the home so the two structures can move independently, preventing cracking in both.
                    </p>
                    <p>
                      <CitationLink num={5} url="https://nj.gov/dca/codes">NJ's Uniform Construction Code</CitationLink> requires a permit for new step construction in most municipalities, and your contractor should handle that before any work begins.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Rebar and Reinforcement Section - Content LEFT, Image RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Rebar and Reinforcement Keep North Jersey Steps <span className="text-gradient-gold">Stable for Decades</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Steel rebar is required for exterior masonry steps. It ties the footing, walls, and treads into a single connected structure. If you skip it, the concrete relies solely on compressive strength. That may hold up fine until frost pushes in sideways or the ground shifts underneath. This is when cracks begin to appear. It starts with the corners, then spreads to where the treads meet the risers.
                    </p>
                    <p>
                      If your steps have 4 or more risers or are wider than 4 feet, reinforcement is a must. Homes in hilly areas such as Wyckoff and West Orange tend to have taller step runs, which increases the load on the structure. North Jersey averages over 80 days below freezing each year. That kind of expansion and contraction breaks unreinforced concrete apart over time.
                    </p>
                    <p>
                      Some people ask if adding extra rocks to the mix makes concrete stronger. There is already aggregate in the mix, and it is a standard ingredient. But gravel can resist compression, and rebar resists tension. They do different jobs, and both are needed. The <CitationLink num={1} url="https://masoncontractors.org/technical">MCAA recommends reinforced construction</CitationLink> for any exterior masonry exposed to freeze-thaw cycles, which is essentially every front step in New Jersey.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-border">
                  <img
                    src={heroImage}
                    alt="Stone masonry steps installation in Verona, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Mortar Type Section - Image LEFT, Content RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="rounded-xl overflow-hidden border border-border">
                  <img
                    src={stoneMontclairImage}
                    alt="Newly installed stone masonry steps and retaining wall in Montclair, Essex County, New Jersey."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Mortar Type and Brick Layout <span className="text-gradient-gold">Determine How Long Steps Last</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Mortar is what bonds the brick or stone veneer to the concrete block body of the steps. Not all mortar is the same. <CitationLink num={3} url="https://store.astm.org/standards/c270">ASTM C270</CitationLink> defines five types based on compressive strength. For exterior steps, Type S is the standard. It provides at least 1,800 PSI and holds up well in wet and cold conditions. Using a weaker type on an outdoor step in North Jersey is a shortcut that will show up later.
                    </p>
                    <p>
                      Brick is the most common step-facing material in Bergen County and in neighborhoods such as Glen Ridge and Morristown. The veneer gets laid in courses over a concrete block core. Stretcher and rowlock courses alternate to form the risers and treads. Each course has to be level and plumb. A mortar bed bonds the brick to the substrate, and in some cases, a scratch coat is applied first to improve adhesion. Joints get tooled after placement to shed water and resist freeze-thaw damage.
                    </p>
                    <p>
                      Timing also matters on the concrete. Fresh concrete should be placed within 90 minutes of batching. After that, the mix starts to set and loses workability. Adding water back in to loosen it up weakens the finished product. Many Bergen County municipalities have aesthetic standards for front entries, so material selection is not just about durability. Your contractor should be able to match the brick or stone to your home's existing style.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Building Code Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Riser Height and Tread Depth Must Meet <span className="text-gradient-gold">New Jersey Building Code</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    Pull a permit for new steps in Essex or Morris County, and the inspector is going to check your measurements. New Jersey adopts the IRC under the <CitationLink num={5} url="https://nj.gov/dca/codes">Uniform Construction Code, N.J.A.C. 5:23</CitationLink>, with state-specific amendments. The numbers are pretty straightforward. The maximum riser height is 7-3/4 inches for residential. Minimum tread depth is 10 inches. And here is the one that trips people up. The height between any two risers in the same flight cannot vary by more than 3/8 of an inch. Even a small difference can change your stride and create a trip hazard, pun intended.
                  </p>
                  <p>
                    <CitationLink num={4} url="https://osha.gov/laws-regs/regulations/standardnumber/1926/1926.1052">Handrails are required</CitationLink> on any stairway with 4 or more risers. Height must be between 34 and 38 inches. If your home is undergoing a sale or inspection, these are the items that are flagged. Buyers in Bergen and Passaic County see it on inspection reports all the time.
                  </p>
                  <p>
                    You may have heard of the "rule of 25" or the "rule of 27." These are comfort guidelines, not code. The rule of 25 states that the sum of riser height and tread depth should be approximately 17 to 18 inches. The rule of 27 says two times the riser height plus the tread depth should land around 24 to 25 inches. Both help create a natural stride, but NJ code sets hard maximums and minimums. Permit requirements vary by town, though most municipalities in North Jersey require one for new masonry steps.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Common Installation Mistakes Section - Content LEFT, Image RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Common Installation Mistakes That Lead to <span className="text-gradient-gold">Early Step Failure</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Most of the step failures we see in Clifton, <Link to="/service-areas/passaic-county/passaic" className="text-secondary hover:underline">Passaic</Link>, and Paterson trace back to the same few shortcuts. No footing. Wrong mortar. Poor drainage. Pick any one of those and the steps are on borrowed time. Pick two and you might be looking at a replacement in under ten years.
                    </p>
                    <p>
                      On the concrete side, pouring on frozen ground is a problem we run into more than you would think. So is adding too much water to the mix to make it easier to work with. It spreads easier, sure. But the finished slab comes out weaker. Skipping control joints is another one. And if concrete is not kept moist for at least 7 days during curing, it never reaches full strength. The 90 minute rule applies here too. Concrete has to be placed within 90 minutes of batching. After that window, adding water back in to re-temper the mix does more harm than good.
                    </p>
                    <p>
                      <CitationLink num={2} url="https://gobrick.com/resources/technical-notes">Brick has its own set of problems</CitationLink>. Laying dry brick without dampening it first pulls moisture out of the mortar too fast. Inconsistent joint thickness looks bad and performs worse. Wrong mortar type for an exterior application, that one speaks for itself. North Jersey's clay soils hold onto moisture, and when winter hits, all that water expands. Tree roots near the foundation can shift footings over time too. Mature oaks and maples are great for shade. Not so great for what is happening underground.
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-border">
                  <img
                    src={stoneStepsFranklinLakesImage}
                    alt="Professional stone steps done by masonry pro construction in Franklin Lakes, Bergen County, New Jersey."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Featured Snippet Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Do You Need a Footing for Masonry Steps in <span className="text-gradient-gold">North Jersey</span>?
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Yes. Masonry steps in North Jersey need a concrete footing below the local frost line. That depth is 36 inches in this part of the state. A footing keeps the steps from shifting when the ground freezes and thaws each winter. Without one, steps can settle, crack, or pull away from the house within a few seasons.
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground list-disc pl-6">
                  <li>The footing should be at least 8 inches thick and rest on undisturbed soil per <CitationLink num={5} url="https://nj.gov/dca/codes">IRC R403</CitationLink></li>
                  <li>Footings must extend a minimum of 12 inches below finished grade</li>
                  <li>New Jersey's Uniform Construction Code (N.J.A.C. 5:23) requires a permit for new step construction in most municipalities</li>
                </ul>
              </div>
            </div>
          </section>

          {/* FAQ Section - Card Grid */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <Card key={index} className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-lg">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <YouTubeShortsSection 
            title="See Our Work in"
            highlightedTitle="Action"
            videoIds={["c_50pnhg30w", "g25VA4vng-Y", "rqWp1V0r8sY"]}
          />

          <RelatedServices currentCategory="masonry" hubOverride={{ href: "/", label: "Masonry Contractor in North Jersey" }} />

          {/* CTA Section with Form */}
          <CTASection 
            heading="Need a" 
            highlightedHeading="Masonry Steps Installation?"
            serviceName="Masonry Steps"
          />

          {/* Citations Accordion */}
          <section className="py-12 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      Citations and References
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base">
                      <ul className="space-y-3">
                        {citations.map((citation) => (
                          <li key={citation.num} className="text-sm">
                            <sup>{citation.num}</sup>{" "}
                            <a 
                              href={citation.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {citation.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
